<script lang="ts">
  /**
   * A short code typed into one box per character — and underneath, one real
   * `<input>`.
   *
   * The boxes are a picture of the input, not inputs of their own: five
   * separate fields break paste, the keyboard's autocorrect row, a screen
   * reader's "edit text" and the backspace that crosses a box. One transparent
   * input laid over the boxes keeps every one of those working, and the boxes
   * only mirror what it holds (the menus proposal's join screen: "5 code boxes
   * mirroring one real input").
   *
   * What a character may be is the caller's to say (`allowed`); by default a
   * letter or a digit. Whatever is typed is folded to upper case, stripped of
   * anything else and cut to `length`, so the value the parent reads is always
   * one it could send.
   *
   * A pasted **link** keeps only its last path segment ("https://…/coop/ABCDE"
   * → ABCDE), and surrounding spaces go. That is why the input carries no
   * `maxlength`: the browser would cut a pasted link or a padded code to five
   * characters *before* this component sees it ("HTTPS", " ABCD") — the fold
   * caps the length instead, after the link and the spaces are dealt with.
   *
   * Two game-supplied steps (since 1.2.0), run on everything typed, pasted or
   * set from outside, in this order:
   *
   * 1. `extract(raw)`: the part of a paste that can be the code. Default
   *    `extractCode` (the last path segment of a link; a fragment or query
   *    after it ignored). A game whose links are `#/join/CODE` passes its own.
   * 2. upper case, then `normalise(code)`: fold look-alikes onto the
   *    alphabet ("O" to "Q", "1" to "J"). Default: nothing. It runs BEFORE
   *    the `allowed` filter, so a character the alphabet refuses can still be
   *    folded onto one it accepts.
   * 3. `allowed` filters single characters, and the result is cut to
   *    `length`.
   *
   * `foldCode` is that pipeline as a plain function, exported, so a game can
   * fold a code from a link the same way before it ever reaches the boxes.
   *
   * The boxes are fixed-size (at most 3.6rem wide, 4:5) and centred, whatever
   * `length` is: a short code is a tight row in the middle, not boxes spread
   * across the column (the v1.1.0 layout gave each box `1fr`).
   */
  import { extractCode, foldCode } from '../code.js';
  interface Props {
    /** The code so far. Bindable: the parent reads it and may set it (Paste). */
    value?: string;
    readonly length: number;
    /** The input's accessible name ("Game code, 5 letters"). */
    readonly label: string;
    /** Which single characters survive; letters and digits by default. */
    readonly allowed?: RegExp;
    /** The part of a paste that can be the code. Default: a link's last path segment. */
    readonly extract?: ((raw: string) => string) | undefined;
    /** Folds look-alikes after upper-casing, before `allowed` filters. Default: none. */
    readonly normalise?: ((code: string) => string) | undefined;
    /** Enter in the box. */
    readonly onsubmit?: (() => void) | undefined;
    readonly autofocus?: boolean;
    readonly disabled?: boolean;
    /**
     * Held while the code is being asked about (since 1.3.0): read-only,
     * `aria-disabled` and `aria-busy`, drawn as disabled, but still
     * focusable, so a focused input keeps the focus (a `disabled` one would
     * drop it to the page). Nothing typed or pasted changes the code.
     */
    readonly busy?: boolean;
    readonly testid?: string | undefined;
  }
  let {
    value = $bindable(''),
    length,
    label,
    allowed = /[A-Z0-9]/,
    extract = extractCode,
    normalise,
    onsubmit,
    autofocus = false,
    disabled = false,
    busy = false,
    testid,
  }: Props = $props();

  let input = $state<HTMLInputElement | null>(null);
  let focused = $state(false);

  function fold(raw: string): string {
    return foldCode(raw, { length, allowed, extract, normalise });
  }

  // A value set from outside (Paste, a link) is folded the same way.
  $effect(() => {
    const folded = fold(value);
    if (folded !== value) value = folded;
  });

  $effect(() => {
    if (autofocus && input !== null) input.focus({ preventScroll: true });
  });

  function onInput(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    if (busy) {
      // Read-only already refuses typing; anything that slips through is undone.
      element.value = value;
      return;
    }
    const folded = fold(element.value);
    if (element.value !== folded) element.value = folded;
    value = folded;
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    onsubmit?.();
  }

  const cells = $derived(Array.from({ length }, (_, index) => value.charAt(index)));
</script>

<label class="ui-code" class:ui-code--focused={focused} class:ui-code--disabled={disabled || busy}>
  <span class="ui-code-boxes" aria-hidden="true">
    {#each cells as char, index (index)}
      <i
        class="ui-code-box"
        class:ui-code-box--filled={char !== ''}
        class:ui-code-box--cursor={focused && index === Math.min(value.length, length - 1)}
        >{char}</i
      >
    {/each}
  </span>
  <input
    bind:this={input}
    class="ui-code-input"
    type="text"
    inputmode="text"
    autocomplete="off"
    autocapitalize="characters"
    spellcheck="false"
    enterkeyhint="go"
    aria-label={label}
    {disabled}
    readonly={busy}
    aria-disabled={busy ? 'true' : undefined}
    aria-busy={busy ? 'true' : undefined}
    {value}
    data-testid={testid}
    oninput={onInput}
    onkeydown={onKeydown}
    onfocus={() => (focused = true)}
    onblur={() => (focused = false)}
  />
</label>

<style>
  .ui-code {
    position: relative;
    display: block;
  }

  /* Fixed boxes, centred: each column at most 3.6rem and shrinking only when
     the row does not fit (a long code on a narrow phone). */
  .ui-code-boxes {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 3.6rem);
    justify-content: center;
    gap: var(--ui-space-2);
  }

  .ui-code-box {
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 4 / 5;
    max-height: 4.5rem;
    border: 2px solid var(--ui-line);
    border-radius: var(--ui-radius-md);
    background: var(--ui-surface);
    color: var(--ui-ink);
    font: var(--ui-text-display);
    font-style: normal;
    font-variant-numeric: tabular-nums;
  }

  .ui-code-box--filled {
    border-color: var(--ui-ink-2);
  }

  .ui-code-box--cursor {
    border-color: var(--ui-accent);
    box-shadow: 0 0 0 2px var(--ui-accent);
  }

  /* The real input: over the boxes, invisible, and still the thing a tap,
     a paste or a screen reader reaches. 16 px so iOS does not zoom. */
  .ui-code-input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0.01;
    border: 0;
    padding: 0;
    font-size: 1rem;
    color: transparent;
    background: transparent;
    caret-color: transparent;
  }

  .ui-code--disabled {
    opacity: 0.6;
  }
</style>
